import { useMemo, useState, type ReactNode } from "react";

export type CatalogCategory = { id: string; name: string; parentCategoryId?: string | null };
export type CategorizedCatalogItem = { id: string; categoryId?: string | null; categoryIds?: string[] };
export function catalogCategoryPath(id: string, categories: Map<string, CatalogCategory>): string {
  const names: string[] = [], seen = new Set<string>();
  let category = categories.get(id);
  while (category && !seen.has(category.id)) {
    seen.add(category.id); names.unshift(category.name);
    category = category.parentCategoryId ? categories.get(category.parentCategoryId) : undefined;
  }
  return names.join(" → ");
}
export function PricebookCategorySections<T extends CategorizedCatalogItem>({ items, categories, renderItem, searchQuery = "", categoryFirst = false }: {
  items: T[]; categories: CatalogCategory[]; renderItem: (item: T) => ReactNode; searchQuery?: string; categoryFirst?: boolean;
}) {
  const normalizedSearch = searchQuery.trim().toLocaleLowerCase();
  const [selection, setSelection] = useState({ id: "", search: normalizedSearch });
  if (selection.search !== normalizedSearch) setSelection({ id: "", search: normalizedSearch });
  const groups = useMemo(() => {
    const byId = new Map(categories.map(category => [category.id, category]));
    const grouped = new Map<string, { id: string; name: string; items: T[] }>();
    for (const item of items) {
      const ids = [...new Set((item.categoryIds?.length ? item.categoryIds : [item.categoryId]).filter((id): id is string => Boolean(id && byId.has(id))))];
      for (const id of ids.length ? ids : ["uncategorized"]) {
        const group = grouped.get(id) ?? { id, name: catalogCategoryPath(id, byId) || "Uncategorized", items: [] };
        group.items.push(item); grouped.set(id, group);
      }
    }
    const rank = (group: { id: string; name: string }) => {
      if (!normalizedSearch) return 0;
      if (group.name.toLocaleLowerCase() === normalizedSearch) return 0;
      if (byId.get(group.id)?.name.toLocaleLowerCase() === normalizedSearch) return 1;
      return 2;
    };
    return [...grouped.values()].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  }, [items, categories, normalizedSearch]);
  // A new search spans the catalog instead of retaining a previous category filter.
  const active = selection.search === normalizedSearch && groups.some(group => group.id === selection.id) ? selection.id : "";
  if (categoryFirst && !normalizedSearch) {
    const selected = groups.find(group => group.id === active);
    return <div className="space-y-3">
      {selected ? <>
        <button type="button" onClick={() => setSelection({id:"",search:normalizedSearch})} className="min-h-11 rounded-lg bg-sky-50 px-3 text-sm font-semibold text-sky-900">Back to categories</button>
        <section aria-label={selected.name} className="space-y-2"><h3 className="text-sm font-semibold">{selected.name}</h3>{selected.items.map(item => <div key={item.id}>{renderItem(item)}</div>)}</section>
      </> : <>
        <p className="text-sm text-slate-600">Choose a category to see its items.</p>
        <div className="grid gap-2 sm:grid-cols-2" aria-label="Pricebook categories">{groups.map(group => <button key={group.id} type="button" aria-label={group.name} onClick={() => setSelection({id:group.id,search:normalizedSearch})} className="flex min-h-16 items-center justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4 text-left text-sm font-semibold text-sky-950 hover:bg-sky-100"><span>{group.name}</span><span className="shrink-0 text-xs font-normal text-slate-600">{group.items.length} {group.items.length === 1 ? 'item' : 'items'}</span></button>)}</div>
      </>}
    </div>;
  }
  return <div className="space-y-3">
    <label className="block text-sm font-semibold">Pricebook category<select value={active} onChange={event => setSelection({ id: event.target.value, search: normalizedSearch })} className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--paper)] p-2"><option value="">All categories</option>{groups.map(group => <option key={group.id} value={group.id}>{group.name} ({group.items.length})</option>)}</select></label>
    {groups.filter(group => !active || group.id === active).map(group => <section key={group.id} aria-label={group.name} className="space-y-2"><h3 className="sticky top-0 bg-[var(--wash)] px-3 py-2 text-sm font-semibold">{group.name}</h3>{group.items.map(item => <div key={item.id}>{renderItem(item)}</div>)}</section>)}
  </div>;
}
